import { useState, useEffect, useCallback } from 'react';

const SECTIONS = ['about', 'skills', 'experience', 'projects', 'education'];

function getScrollParent(el: HTMLElement): HTMLElement | Window {
  let parent = el.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

function getScrollTop(target: HTMLElement | Window): number {
  return target instanceof Window ? target.scrollY : target.scrollTop;
}

function getViewportHeight(target: HTMLElement | Window): number {
  return target instanceof Window ? target.innerHeight : target.clientHeight;
}

export function useScrollSpy() {
  const [activeSection, setActiveSection] = useState('about');

  useEffect(() => {
    // Kısa bir gecikmeyle scroll parent'ı bul
    let scrollTarget: HTMLElement | Window = window;
    const timer = setTimeout(() => {
      const firstSection = document.getElementById(SECTIONS[0]);
      if (firstSection) {
        scrollTarget = getScrollParent(firstSection);
      }

      const handleScroll = () => {
        const scrollTop = getScrollTop(scrollTarget);
        const vh = getViewportHeight(scrollTarget);
        const viewportMiddle = scrollTop + vh / 2;

        let closest = SECTIONS[0];
        let minDist = Infinity;

        for (const id of SECTIONS) {
          const el = document.getElementById(id);
          if (!el) continue;

          let top: number;
          if (scrollTarget instanceof Window) {
            top = el.getBoundingClientRect().top + window.scrollY;
          } else {
            top = el.offsetTop;
          }
          const mid = top + el.offsetHeight / 2;
          const dist = Math.abs(viewportMiddle - mid);
          if (dist < minDist) {
            minDist = dist;
            closest = id;
          }
        }

        setActiveSection(closest);
      };

      const target = scrollTarget instanceof Window ? window : scrollTarget;
      target.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();

      // Cleanup kaydı
      cleanupRef = () => {
        target.removeEventListener('scroll', handleScroll);
      };
    }, 150);

    let cleanupRef: (() => void) | null = null;

    return () => {
      clearTimeout(timer);
      if (cleanupRef) cleanupRef();
    };
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.getSelection()?.removeAllRanges();

    // URL hash güncelle (?mode=classic korunarak)
    const url = new URL(window.location.href);
    url.hash = id;
    window.history.replaceState(null, '', url.toString());

    // scrollIntoView her durumda çalışır — container ne olursa olsun
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setActiveSection(id);
  }, []);

  return { activeSection, scrollToSection, sections: SECTIONS };
}
